import React, { useEffect, useRef, useState } from "react";
import type { ChatbotMessageType } from "@/types";
import { Bot } from "lucide-react";
import { motion } from "framer-motion";
import Typewriter from "./typewriter";
import { Avatar } from "@/components/ui/avatar";
import type Widget from "@/model/widget";
import { CHATBOT_COMPONENT_NAME } from "@/constants";
import WidgetLoader from "../widget/widget-loader";
import ChatbotServiceProvider from "./chatbot-service-provider";
import ChatbotBenefitDocument from "./chatbot-benefit-document";
import ChatbotMemberMedication from "./chatbot-member-medication";
import RelatedQuestions from "./chatbot-related-questions";

type ChatbotMessageProps = {
  message: ChatbotMessageType;
  recentMessageId: string;
  postUserInput: (query: string) => void;
  onTyping: (typing: boolean) => void;
  typingInterruptRef: React.MutableRefObject<boolean>;
};

type RenderComponentProps = {
  widget: Widget[];
  componentName: string;
  componentData: unknown;
};

const RenderComponent: React.FC<RenderComponentProps> = (props) => {
  const { widget, componentName, componentData } = props;
  switch (componentName) {
    case CHATBOT_COMPONENT_NAME.PLAN_SERVICE_DETAILS:
      return <WidgetLoader widget={widget[0]} />;
    case CHATBOT_COMPONENT_NAME.PROVIDER_DETAILS:
      return (
        <ChatbotServiceProvider
          phone={componentData?.phone}
          serviceProvider={componentData?.service_provider}
          website={componentData?.website}
          email={componentData?.email}
        />
      );
    case CHATBOT_COMPONENT_NAME.BENEFIT_DOCUMENT:
      return (
        <ChatbotBenefitDocument
          documentName="Benefit Document"
          documentLink={componentData?.document_link}
        />
      );
    case CHATBOT_COMPONENT_NAME.MEMBER_MEDICATION:
      return <ChatbotMemberMedication drugs={componentData} />;
    default:
      return <WidgetLoader widget={widget[0]} />;
  }
};

const ChatbotMessage: React.FC<ChatbotMessageProps> = (props) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const {
    message,
    recentMessageId,
    postUserInput,
    onTyping,
    typingInterruptRef,
  } = props;
  const [showComponent, setShowComponent] = useState<boolean>(false);

  useEffect(() => {
    if (!message.textMessage) {
      setShowComponent(true);
    }
  }, [message.textMessage]);

  useEffect(() => {
    if (showComponent) {
      containerRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [showComponent]);

  const onRelatedQuestionSelect = (question: string) => {
    postUserInput(question);
  };

  return (
    <div
      className={`mb-2 flex flex-col ${message.isUser ? "items-end" : "items-start"}`}
    >
      {message.isUser ? (
        <>
          <div className="max-w-[90%] rounded-lg bg-primary p-2 px-3 text-white">
            <p className="whitespace-pre-wrap break-words text-medium text-white">
              {message.textMessage}
            </p>
          </div>
          <p className="mr-2 mt-1 text-right text-xs text-gray-400">
            {message.timestamp}
          </p>
        </>
      ) : (
        <>
          <Avatar className="mb-2 size-10 flex-shrink-0">
            <div className="flex size-full items-center justify-center rounded-full bg-orange-200 text-orange-700">
              <Bot className="size-6" />
            </div>
          </Avatar>
          {message?.textMessage?.length > 0 ? (
            <div className="max-w-[90%] rounded-lg bg-gray-200 p-2 px-3">
              {message?.id === "1" ? (
                <p className="whitespace-pre-wrap text-medium text-gray-700">
                  {message.textMessage}
                </p>
              ) : (
                <Typewriter
                  text={message.textMessage}
                  onDone={() => {
                    setShowComponent(true);
                    typingInterruptRef.current = false;
                  }}
                  onTyping={onTyping}
                  interruptRef={typingInterruptRef}
                />
              )}
            </div>
          ) : null}
          {message.containsComponentToRender && showComponent && (
            <motion.div
              className="mt-2 w-full"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              <RenderComponent
                componentData={message?.component?.componentData}
                componentName={message?.component!.componentName}
                widget={message?.component!.widget}
              />
            </motion.div>
          )}
          {showComponent && (
            <>
              {Array.isArray(message.relatedQuestions) &&
                message.relatedQuestions.length > 0 &&
                message.id === recentMessageId && (
                  <RelatedQuestions
                    questions={message.relatedQuestions}
                    onQuestionSelect={onRelatedQuestionSelect}
                  />
                )}
              <p className="ml-2 mt-1 text-xs text-gray-500">
                {message.timestamp}
              </p>
              <div ref={containerRef} />
            </>
          )}
        </>
      )}
    </div>
  );
};

export default React.memo(ChatbotMessage);
